import { Component } from '@theme/component';
import { onAnimationEnd } from '@theme/utilities';

/**
 * Focusable candidates, before the `inert` and visibility filters in
 * `#focusableElements`. Kept local rather than reusing `@theme/focus`, whose
 * `trapFocus` snapshots first/last once and cannot see `inert` — with sliding
 * levels that snapshot is stale the moment a level changes, and Tab lands on an
 * element the browser refuses to focus.
 */
const FOCUSABLE_SELECTOR =
  "summary, a[href], button:enabled, [tabindex]:not([tabindex^='-']), input:not([type=hidden]):enabled, select:enabled, textarea:enabled";

/**
 * A custom element that manages the main menu drawer.
 *
 * Two levels: a root list and one sub-level per top-level link with children.
 * Levels are siblings inside the panel, and every level except the active one
 * carries `inert`, which is what keeps focus and pointer events out of the
 * levels sliding past. See `snippets/header-drawer.liquid` for the markup and
 * `material/menu-example/IMPACT-MENU-SPEC.md` for what this is modelled on.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element wrapping the whole drawer.
 * @property {HTMLDivElement} menuDrawer - The fixed overlay holding the sheet.
 * @property {HTMLDivElement} panel - The card. Owns `data-active-level`.
 * @property {HTMLElement[]} levels - The root level plus every sub-level.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details', 'menuDrawer', 'panel'];

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener('keydown', this.#onKeydown, true);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('keydown', this.#onKeydown, true);
  }

  /**
   * Whether the drawer is open.
   *
   * Deliberately the `menu-open` class rather than the `open` attribute: `open`
   * is held for the whole closing transition so the sheet stays painted while
   * it animates out, and reading it here would mean the hamburger is dead for
   * that quarter second, the reopen-during-close guard below can never fire,
   * and Tab stays trapped in a sheet the user can no longer see.
   *
   * @returns {boolean}
   */
  get isOpen() {
    return this.refs.details.classList.contains('menu-open');
  }

  /** @returns {string} The id of the level currently on screen — `'root'` or a level id */
  get activeLevel() {
    return this.refs.panel.dataset.activeLevel ?? 'root';
  }

  /** @returns {HTMLElement | undefined} */
  get #summary() {
    return this.refs.details.querySelector('summary') ?? undefined;
  }

  /**
   * Toggle the drawer.
   *
   * The default action is prevented so `open` is ours to set: the browser would
   * otherwise unset it on the same click that starts the closing transition,
   * hiding the sheet before it has animated anywhere.
   *
   * @param {Event} [event]
   */
  toggle(event) {
    event?.preventDefault();

    return this.isOpen ? this.close() : this.open();
  }

  /** Open the drawer at its root level. */
  open() {
    const { details } = this.refs;

    if (this.isOpen) return;

    this.#showLevel('root');
    details.open = true;
    this.#summary?.setAttribute('aria-expanded', 'true');

    // Let the browser paint the closed state once, so the transition has a
    // starting value to run from.
    requestAnimationFrame(() => {
      details.classList.add('menu-open');
    });
  }

  /** Close the drawer, then reset it to the root level. */
  close() {
    const { details, panel } = this.refs;

    if (!this.isOpen) return;

    details.classList.remove('menu-open');
    this.#summary?.setAttribute('aria-expanded', 'false');

    onAnimationEnd(
      panel,
      () => {
        // A reopen during the closing transition must win.
        if (details.classList.contains('menu-open')) return;

        details.open = false;
        this.#showLevel('root');
      },
      { subtree: false }
    );

    /* ⚠️ `preventScroll` IS THE WHOLE POINT OF THIS LINE'S ARGUMENT, and it
       was missing until 2026-09-22. Returning focus to the opener is correct
       and stays — a keyboard or screen-reader user must land back where they
       were. But the summary is the hamburger up in the header, and focusing
       an element that is off-screen makes the browser scroll it into view. On
       a page scrolled halfway down, closing the menu therefore threw the
       reader back to the very top. `preventScroll` keeps the focus and drops
       the scroll. The same idiom is already used in #focusFirst below. */
    this.#summary?.focus({ preventScroll: true });
  }

  /**
   * Slide a sub-level in.
   *
   * @param {string | number} levelId - The `data-level-id` of the level to show
   * @param {Event} [event]
   */
  openLevel(levelId, event) {
    event?.preventDefault();

    const level = this.#getLevel(String(levelId));

    if (!level) return;

    this.#showLevel(String(levelId));

    // Impact leaves focus on the button it just hid; a screen reader is then
    // announcing nothing. Move it to the back button, which names the level.
    this.#focusWhenShown(level.querySelector('.menu-drawer__back-button'));
  }

  /**
   * Slide back to the root level.
   *
   * @param {Event} [event]
   */
  closeLevel(event) {
    event?.preventDefault();

    const leaving = this.activeLevel;

    if (leaving === 'root') return this.close();

    this.#showLevel('root');
    this.#focusWhenShown(this.#getTrigger(leaving));
  }

  /**
   * Focus an element that has just been un-hidden.
   *
   * Reading a layout property first forces the pending style recalculation, so
   * the element's `visibility` is settled before the browser decides whether it
   * is focusable — without it, `.focus()` is a silent no-op on the frame the
   * level changes.
   *
   * @param {Element | null} element
   */
  #focusWhenShown(element) {
    if (!(element instanceof HTMLElement)) return;

    requestAnimationFrame(() => {
      void element.offsetHeight;
      element.focus({ preventScroll: true });
    });
  }

  /**
   * Put one level on screen and take every other one out of the accessibility
   * tree. `inert` covers focus, pointer events and screen-reader exposure in
   * one attribute, so nothing here has to be hand-rolled.
   *
   * @param {string} levelId
   */
  #showLevel(levelId) {
    const { panel } = this.refs;
    const levels = this.refs.levels ?? [];

    panel.dataset.activeLevel = levelId;

    for (const level of levels) {
      const isActive = (level.dataset.levelId ?? 'root') === levelId;

      level.classList.toggle('menu-drawer__level--active', isActive && levelId !== 'root');
      level.toggleAttribute('inert', !isActive);
    }

    for (const trigger of this.querySelectorAll('[aria-controls][aria-expanded]')) {
      const controlled = document.getElementById(trigger.getAttribute('aria-controls') ?? '');
      const expanded = controlled != null && (controlled.dataset.levelId ?? '') === levelId;

      trigger.setAttribute('aria-expanded', String(expanded));
    }
  }

  /**
   * @param {string} levelId
   * @returns {HTMLElement | undefined}
   */
  #getLevel(levelId) {
    return (this.refs.levels ?? []).find((level) => (level.dataset.levelId ?? 'root') === levelId);
  }

  /**
   * @param {string} levelId
   * @returns {HTMLElement | null}
   */
  #getTrigger(levelId) {
    const level = this.#getLevel(levelId);

    return level ? this.querySelector(`[aria-controls="${CSS.escape(level.id)}"]`) : null;
  }

  /**
   * Escape steps back one level before it closes the drawer, and Tab is kept
   * inside the sheet. Impact's Escape closes outright from any depth, which
   * loses the user's place for no reason.
   *
   * @param {KeyboardEvent} event
   */
  #onKeydown = (event) => {
    if (!this.isOpen) return;

    if (event.key === 'Escape') {
      event.preventDefault();

      if (this.activeLevel === 'root') this.close();
      else this.closeLevel();

      return;
    }

    if (event.key !== 'Tab') return;

    const focusable = this.#focusableElements();
    const first = focusable[0];
    const last = focusable[focusable.length - 1];

    if (!first || !last) return;

    const active = document.activeElement;
    const inside = active instanceof Node && this.refs.menuDrawer.contains(active);

    if (event.shiftKey && (!inside || active === first)) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && (!inside || active === last)) {
      event.preventDefault();
      first.focus();
    }
  };

  /**
   * The focusable elements currently on screen inside the sheet, recomputed on
   * every Tab so a level change can never leave a stale boundary behind.
   *
   * @returns {HTMLElement[]}
   */
  #focusableElements() {
    return Array.from(this.refs.menuDrawer.querySelectorAll(FOCUSABLE_SELECTOR)).filter((element) => {
      if (!(element instanceof HTMLElement)) return false;
      if (element.closest('[inert]')) return false;

      return element.checkVisibility?.({ visibilityProperty: true }) ?? true;
    });
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}
